import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Database,
  FileText,
  Link2,
  Loader2,
  Sparkles,
} from 'lucide-react'
import { FreshnessRibbon } from '@/components/FreshnessRibbon'
import { useAdapters } from '@/hooks/useAdapters'
import { useUploadHistory } from '@/hooks/useBox'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { useProposals } from '@/hooks/useProposals'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import { useAppAuth } from '@/lib/auth'
import type { ProposalListItem, UploadStatus } from '@/types/api'

function formatRelativeTime(value?: string) {
  if (!value) return '時刻未取得'

  const diffMs = new Date(value).getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))

  if (Math.abs(diffMinutes) < 1) return 'たった今'
  if (Math.abs(diffMinutes) < 60) {
    return new Intl.RelativeTimeFormat('ja', { numeric: 'auto' }).format(diffMinutes, 'minute')
  }

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 24) {
    return new Intl.RelativeTimeFormat('ja', { numeric: 'auto' }).format(diffHours, 'hour')
  }

  const diffDays = Math.round(diffHours / 24)
  return new Intl.RelativeTimeFormat('ja', { numeric: 'auto' }).format(diffDays, 'day')
}

function formatUploadStatus(status: UploadStatus) {
  switch (status) {
    case 'pending':
      return '待機中'
    case 'processing':
      return '処理中'
    case 'completed':
      return '完了'
    case 'failed':
      return '失敗'
    case 'cancelled':
      return 'キャンセル済み'
    default:
      return status
  }
}

function uploadStatusTone(status: UploadStatus) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'failed':
      return 'bg-red-100 text-red-800'
    case 'cancelled':
      return 'bg-amber-100 text-amber-800'
    default:
      return 'bg-slate-100 text-slate-700'
  }
}

function proposalStatusLabel(status: ProposalListItem['status']) {
  switch (status) {
    case 'pending':
      return '未対応'
    case 'approved':
      return '承認済み'
    case 'rejected':
      return '却下済み'
    case 'expired':
      return '期限切れ'
    case 'executed':
      return '実行済み'
    case 'failed':
      return '失敗'
    case 'draft':
      return '下書き'
    default:
      return status
  }
}

function proposalStatusTone(status: ProposalListItem['status']) {
  switch (status) {
    case 'pending':
      return 'bg-amber-100 text-amber-800'
    case 'approved':
    case 'executed':
      return 'bg-green-100 text-green-800'
    case 'rejected':
    case 'failed':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-slate-100 text-slate-700'
  }
}

function formatPriority(priority: number) {
  if (priority >= 80) return '高'
  if (priority >= 50) return '中'
  return '低'
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FirstValueCard({
  icon: Icon,
  title,
  value,
  description,
  isLoading,
}: {
  icon: React.ElementType
  title: string
  value: string
  description: string
  isLoading?: boolean
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="mt-2 text-3xl font-bold">
            {isLoading ? <span className="text-muted-foreground">—</span> : value}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="rounded-full bg-primary/10 p-3 text-primary">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
        </div>
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAppAuth()
  const resolvedWorkspace = useResolvedWorkspace(import.meta.env.VITE_DEFAULT_WORKSPACE_ID)
  const workspaceId = resolvedWorkspace.workspaceId
  const workspace = resolvedWorkspace.workspace
  const workspaceName = workspace?.name ?? workspaceId

  const onboardingQuery = useOnboardingStatus(workspaceId)
  const adaptersQuery = useAdapters(workspaceId)
  const historyQuery = useUploadHistory(workspaceId, 1, 5)
  const pendingProposalsQuery = useProposals(
    {
      workspaceId,
      status: ['pending'],
      limit: 5,
    },
    { enabled: workspaceId.length > 0 }
  )
  const insightsQuery = useProposals(
    {
      workspaceId,
      limit: 3,
    },
    { enabled: workspaceId.length > 0 }
  )

  const adapters = adaptersQuery.data ?? []
  const connectedAdapters = adapters.filter((adapter) => adapter.status === 'connected')
  const recentUploads = historyQuery.data?.items ?? []
  const processingUploads = recentUploads.filter(
    (item) => item.status === 'pending' || item.status === 'processing'
  )
  const pendingProposalTotal = pendingProposalsQuery.data?.total ?? 0
  const latestInsights = insightsQuery.data?.items ?? []
  const onboardingStatus = onboardingQuery.data

  const connectionDescription = connectedAdapters.length > 0
    ? connectedAdapters.map((adapter) => adapter.name).slice(0, 2).join(' / ')
    : 'LINE や Google など、最初のソース接続が必要です。'

  const documentDescription = recentUploads[0]
    ? `${recentUploads[0].filename} を ${formatRelativeTime(recentUploads[0].completedAt ?? recentUploads[0].createdAt)} に処理`
    : 'まだファイル投入はありません。BOX から追加できます。'

  const insightDescription = latestInsights[0]
    ? latestInsights[0].summary ?? latestInsights[0].title ?? '最新の提案を確認できます。'
    : '提案がたまると、ここに重要な気づきが並びます。'

  const pendingDescription = pendingProposalTotal > 0
    ? '優先度が高いものから確認していくと運用に乗せやすくなります。'
    : '今は確認待ちの提案はありません。'

  const nextActions = [
    connectedAdapters.length === 0
      ? {
          title: '最初のデータソースを接続する',
          description: 'LINE や Google などをつなぐと、継続的にデータが入り始めます。',
          href: '/adapters',
        }
      : null,
    (onboardingStatus?.documentCount ?? 0) === 0
      ? {
          title: '最初のファイルを入れる',
          description: 'PDF・Markdown・JSON・CSV を BOX に投入して分析を開始します。',
          href: '/box',
        }
      : null,
    processingUploads.length > 0
      ? {
          title: '処理中の投入を確認する',
          description: `${processingUploads.length} 件が処理中です。完了すると提案や履歴に反映されます。`,
          href: '/box',
        }
      : null,
    pendingProposalTotal > 0
      ? {
          title: '未対応の提案を確認する',
          description: `${pendingProposalTotal} 件の提案が残っています。重要度の高い順に確認できます。`,
          href: '/proposals',
        }
      : null,
  ].filter((action): action is { title: string; description: string; href: string } => Boolean(action))

  const activeErrors = [
    resolvedWorkspace.error,
    onboardingQuery.error,
    adaptersQuery.error,
    historyQuery.error,
    pendingProposalsQuery.error,
    insightsQuery.error,
  ].filter(Boolean)

  if (resolvedWorkspace.isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-lg border bg-card p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> workspace を解決しています...
      </div>
    )
  }

  if (!workspaceId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        利用可能な workspace がありません。先に Onboarding で workspace を作成してください。
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">ダッシュボード</h1>
          <p className="text-muted-foreground">
            {user?.firstName ?? 'ユーザー'}さん向けに、いまの投入状況と次に見るべき項目をまとめています。
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            対象 workspace: <span className="font-medium text-foreground">{workspaceName}</span>
          </p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">
          <p className="font-medium text-foreground">いまの導入状況</p>
          <p className="mt-1">
            heartbeat: {onboardingStatus?.heartbeatEnabled ? '有効' : '未起動'}
          </p>
          <p>
            最近の投入: {recentUploads[0] ? formatUploadStatus(recentUploads[0].status) : '未投入'}
          </p>
        </div>
      </div>

      <FreshnessRibbon />

      {activeErrors.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5" />
          <div>
            <p className="font-medium">一部のダッシュボード情報を取得できませんでした。</p>
            <p className="mt-1 text-xs text-amber-800">
              接続・投入・提案のどれかがまだ準備中の可能性があります。利用可能なデータから先に表示しています。
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <FirstValueCard
          icon={Link2}
          title="接続済みソース"
          value={`${onboardingStatus?.connectedAdapters ?? connectedAdapters.length}`}
          description={connectionDescription}
          isLoading={onboardingQuery.isLoading || adaptersQuery.isLoading}
        />
        <FirstValueCard
          icon={Database}
          title="蓄積ドキュメント"
          value={`${onboardingStatus?.documentCount ?? 0}`}
          description={documentDescription}
          isLoading={onboardingQuery.isLoading || historyQuery.isLoading}
        />
        <FirstValueCard
          icon={Sparkles}
          title="最新の重要示唆"
          value={`${latestInsights.length}件`}
          description={insightDescription}
          isLoading={insightsQuery.isLoading}
        />
        <FirstValueCard
          icon={ClipboardList}
          title="未対応提案"
          value={`${pendingProposalTotal}件`}
          description={pendingDescription}
          isLoading={pendingProposalsQuery.isLoading}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
        <section className="rounded-xl border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b pb-4">
            <div>
              <h2 className="font-semibold">最新の重要示唆</h2>
              <p className="text-sm text-muted-foreground">
                直近の提案から、判断や対応につながる項目を優先表示しています。
              </p>
            </div>
            <Link
              to="/proposals"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              提案一覧へ <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-4 space-y-3">
            {insightsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> 提案を読み込み中...
              </div>
            )}
            {!insightsQuery.isLoading && latestInsights.length === 0 && (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                重要な気づきはまだありません。データソース接続か BOX 投入が進むと、ここに提案が表示されます。
              </div>
            )}
            {latestInsights.map((proposal) => (
              <Link
                key={proposal.id}
                to={`/proposals/${proposal.id}`}
                className="block rounded-lg border p-4 transition-colors hover:bg-muted/30"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-foreground">{proposal.title ?? '無題の提案'}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {proposal.summary ?? '詳細画面で根拠と本文を確認できます。'}
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${proposalStatusTone(proposal.status)}`}>
                    {proposalStatusLabel(proposal.status)}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>優先度: {formatPriority(proposal.priority)}</span>
                  <span>スコープ: {proposal.actionScope}</span>
                  <span>{formatRelativeTime(proposal.createdAt)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <div className="border-b pb-4">
              <h2 className="font-semibold">今やると価値が出ること</h2>
              <p className="text-sm text-muted-foreground">
                いまの状態に応じて、次にやると前進しやすい順に並べています。
              </p>
            </div>

            <div className="mt-4 space-y-3">
              {nextActions.length === 0 && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                  接続・投入・提案確認の基本導線は進んでいます。次は提案の採択や継続投入で運用精度を上げる段階です。
                </div>
              )}
              {nextActions.map((action) => (
                <Link
                  key={action.title}
                  to={action.href}
                  className="block rounded-lg border p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-foreground">{action.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{action.description}</p>
                    </div>
                    <ArrowRight className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <div className="border-b pb-4">
              <h2 className="font-semibold">最近の投入</h2>
              <p className="text-sm text-muted-foreground">
                BOX に入れたファイルの処理状況をここからすぐ確認できます。
              </p>
            </div>

            <div className="mt-4 space-y-3">
              {historyQuery.isLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> 履歴を読み込み中...
                </div>
              )}
              {!historyQuery.isLoading && recentUploads.length === 0 && (
                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                  まだ投入履歴はありません。最初のファイルを入れると、ここに処理状況が出ます。
                </div>
              )}
              {recentUploads.map((item) => (
                <div key={item.uploadId} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">{item.filename}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatSize(item.size)} ・ {formatRelativeTime(item.completedAt ?? item.createdAt)}
                        </p>
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${uploadStatusTone(item.status)}`}>
                      {formatUploadStatus(item.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-slate-950 p-6 text-slate-50 shadow-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-300" />
              <div>
                <p className="font-medium">この画面で分かること</p>
                <p className="mt-2 text-sm text-slate-300">
                  いま何が接続され、どれだけ蓄積され、どんな提案が出ていて、次に何を確認すべきかを最短で把握できます。
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
